import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, Plus, Trash2, GripVertical, Check, X, AlertTriangle, ChevronDown, ChevronUp, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';

const GATE_COLORS = ['#1E4E8C', '#143A6B', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#6366F1'];

function WeightageBar({ gates }) {
  const total = gates.reduce((s, g) => s + (parseFloat(g.weightage_percent) || 0), 0);
  const isValid = Math.abs(total - 100) < 0.01;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span style={{ color: 'var(--text-muted)' }}>Weightage Distribution</span>
        <span className="font-medium" style={{ color: isValid ? 'var(--status-completed-text)' : 'var(--status-delayed-text)' }}>
          Total: {total.toFixed(1)}% {isValid ? <Check className="w-3 h-3 inline" /> : <AlertTriangle className="w-3 h-3 inline" />}
        </span>
      </div>
      <div className="h-3 rounded-full overflow-hidden flex" style={{ backgroundColor: 'var(--border-color)' }}>
        {gates.map((gate, idx) => {
          const width = parseFloat(gate.weightage_percent) || 0;
          return (
            <div key={gate.stage_gate_id || idx} title={`${gate.stage_gate_name}: ${width}%`}
              style={{ width: `${width}%`, backgroundColor: GATE_COLORS[idx % GATE_COLORS.length], height: '100%', transition: 'width 0.3s ease',
                borderRight: idx < gates.length - 1 ? '2px solid var(--card-bg)' : 'none' }} />
          );
        })}
      </div>
      <div className="flex gap-2 flex-wrap">
        {gates.map((gate, idx) => (
          <div key={gate.stage_gate_id || idx} className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: GATE_COLORS[idx % GATE_COLORS.length] }} />
            {gate.stage_gate_name} ({gate.weightage_percent}%)
          </div>
        ))}
      </div>
    </div>
  );
}

function StageGateEditor({ config, docType, projectId, onSaved, onCancel }) {
  const [gates, setGates] = useState(
    config ? config.stage_gates.map((sg) => ({ ...sg }))
      : [{ stage_gate_id: `SG-${Date.now().toString(36).toUpperCase().slice(-6)}-01`, stage_gate_name: '', sequence_order: 1, weightage_percent: 100, description: '', is_milestone: false, is_terminal: true }]
  );
  const [saving, setSaving] = useState(false);

  const addGate = () => {
    const updated = gates.map((g) => ({ ...g, is_terminal: false }));
    updated.push({ stage_gate_id: `SG-${Date.now().toString(36).toUpperCase().slice(-6)}-${String(updated.length + 1).padStart(2, '0')}`, stage_gate_name: '', sequence_order: updated.length + 1, weightage_percent: 0, description: '', is_milestone: false, is_terminal: true });
    setGates(updated);
  };

  const removeGate = (index) => {
    if (gates.length <= 1) { toast.error('At least one stage gate is required'); return; }
    const updated = gates.filter((_, i) => i !== index);
    updated.forEach((g, i) => { g.is_terminal = i === updated.length - 1; g.sequence_order = i + 1; });
    setGates(updated);
  };

  const updateGate = (index, field, value) => {
    const updated = [...gates];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'is_terminal') updated.forEach((g, i) => { g.is_terminal = i === index; });
    setGates(updated);
  };

  const distributeEvenly = () => {
    const count = gates.length;
    const w = parseFloat((100 / count).toFixed(1));
    setGates(gates.map((g, i) => ({ ...g, weightage_percent: i === count - 1 ? parseFloat((100 - w * (count - 1)).toFixed(1)) : w })));
  };

  const handleSave = async () => {
    const total = gates.reduce((s, g) => s + (parseFloat(g.weightage_percent) || 0), 0);
    if (Math.abs(total - 100) > 0.01) { toast.error(`Weightages must sum to 100%. Current: ${total.toFixed(1)}%`); return; }
    if (gates.some((g) => !g.stage_gate_name.trim())) { toast.error('All gates must have a name'); return; }
    if (gates.filter((g) => g.is_terminal).length !== 1) { toast.error('Exactly one gate must be terminal'); return; }
    setSaving(true);
    try {
      const payload = { stage_gates: gates.map((g) => ({ stage_gate_id: g.stage_gate_id, stage_gate_name: g.stage_gate_name.trim(), sequence_order: g.sequence_order, weightage_percent: parseFloat(g.weightage_percent), description: g.description || '', is_milestone: g.is_milestone, is_terminal: g.is_terminal })) };
      if (config) { await api.put(`/projects/${projectId}/engineering-schedule/stage-gate-configs/${config.id}`, payload); }
      else { payload.document_type_id = docType.id; await api.post(`/projects/${projectId}/engineering-schedule/stage-gate-configs`, payload); }
      toast.success('Configuration saved');
      if (onSaved) onSaved();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="stage-gate-editor">
      <WeightageBar gates={gates} />
      <div className="space-y-2">
        {gates.map((gate, index) => (
          <div key={gate.stage_gate_id} className="flex items-start gap-3 p-3 rounded-lg group"
            style={{ backgroundColor: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}>
            <div className="flex items-center gap-1 pt-2" style={{ color: 'var(--text-disabled)' }}>
              <GripVertical className="w-4 h-4" /><span className="text-xs font-mono">{index + 1}</span>
            </div>
            <div className="flex-1 grid grid-cols-12 gap-3">
              <div className="col-span-3">
                <label className="text-[10px] uppercase mb-1 block" style={{ color: 'var(--text-disabled)' }}>Gate Name</label>
                <Input data-testid={`gate-name-${index}`} value={gate.stage_gate_name} onChange={(e) => updateGate(index, 'stage_gate_name', e.target.value)} placeholder="e.g. IFR, IFA, IFC"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} className="text-sm h-8" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase mb-1 block" style={{ color: 'var(--text-disabled)' }}>Weight %</label>
                <Input data-testid={`gate-weight-${index}`} type="number" min="0" max="100" step="0.1" value={gate.weightage_percent} onChange={(e) => updateGate(index, 'weightage_percent', parseFloat(e.target.value) || 0)}
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} className="text-sm h-8" />
              </div>
              <div className="col-span-4">
                <label className="text-[10px] uppercase mb-1 block" style={{ color: 'var(--text-disabled)' }}>Description</label>
                <Input value={gate.description || ''} onChange={(e) => updateGate(index, 'description', e.target.value)} placeholder="Optional"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} className="text-sm h-8" />
              </div>
              <div className="col-span-3 flex items-end gap-2 pb-0.5">
                <label className="flex items-center gap-1.5 cursor-pointer text-xs" style={{ color: 'var(--text-muted)' }}>
                  <input type="checkbox" checked={gate.is_milestone} onChange={(e) => updateGate(index, 'is_milestone', e.target.checked)} className="rounded" />
                  Milestone
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs" style={{ color: 'var(--text-muted)' }}>
                  <input type="checkbox" checked={gate.is_terminal} onChange={(e) => updateGate(index, 'is_terminal', e.target.checked)} className="rounded" />
                  Terminal
                </label>
              </div>
            </div>
            <button onClick={() => removeGate(index)} data-testid={`remove-gate-${index}`}
              className="pt-6 transition-colors opacity-0 group-hover:opacity-100" style={{ color: 'var(--text-disabled)' }}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={addGate} data-testid="add-gate-btn" style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)' }}>
            <Plus className="w-4 h-4 mr-1" /> Add Gate
          </Button>
          <Button variant="outline" size="sm" onClick={distributeEvenly} data-testid="distribute-weight-btn" style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)' }}>
            Distribute Evenly
          </Button>
        </div>
        <div className="flex gap-2">
          {onCancel && <Button variant="ghost" size="sm" onClick={onCancel} style={{ color: 'var(--text-muted)' }}>Cancel</Button>}
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#1E4E8C] hover:bg-[#2850EE] text-white" data-testid="save-config-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Configuration
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function StageGateConfig({ projectId, onClose }) {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [stageGateConfigs, setStageGateConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedType, setExpandedType] = useState(null);
  const [showAddDocType, setShowAddDocType] = useState(false);
  const [newDocType, setNewDocType] = useState({ code: '', name: '', description: '', discipline: '' });
  const [addingDocType, setAddingDocType] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dtRes, sgRes] = await Promise.all([
        api.get(`/projects/${projectId}/engineering-schedule/document-types`),
        api.get(`/projects/${projectId}/engineering-schedule/stage-gate-configs`),
      ]);
      setDocumentTypes(dtRes.data);
      setStageGateConfigs(sgRes.data);
    } catch (error) { toast.error('Failed to load configuration'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddDocType = async () => {
    if (!newDocType.code || !newDocType.name) { toast.error('Code and Name are required'); return; }
    setAddingDocType(true);
    try {
      await api.post(`/projects/${projectId}/engineering-schedule/document-types`, newDocType);
      toast.success('Document type created');
      setShowAddDocType(false);
      setNewDocType({ code: '', name: '', description: '', discipline: '' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create'); }
    finally { setAddingDocType(false); }
  };

  const handleDeleteDocType = async (dtId) => {
    if (!window.confirm('Delete this document type?')) return;
    try { await api.delete(`/projects/${projectId}/engineering-schedule/document-types/${dtId}`); toast.success('Deleted'); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete'); }
  };

  const getConfigForDocType = (dtId) => stageGateConfigs.find((c) => c.document_type_id === dtId);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent)' }} /></div>;

  return (
    <div className="space-y-6" data-testid="stage-gate-config-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--accent-bg)', border: '1px solid rgba(56,96,255,0.2)' }}>
            <Settings className="w-5 h-5" style={{ color: 'var(--accent-text)' }} />
          </div>
          <div>
            <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Stage Gate Configuration</h2>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Define document types and their stage gate workflows</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAddDocType(!showAddDocType)} data-testid="add-doc-type-btn"
            style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)' }}>
            <Plus className="w-4 h-4 mr-1" /> Add Document Type
          </Button>
          {onClose && <Button variant="ghost" size="sm" onClick={onClose} style={{ color: 'var(--text-muted)' }}><X className="w-4 h-4" /></Button>}
        </div>
      </div>

      <AnimatePresence>
        {showAddDocType && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="rounded-xl p-4 space-y-3" style={{ backgroundColor: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}>
              <h3 className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>New Document Type</h3>
              <div className="grid grid-cols-4 gap-3">
                <div><label className="text-[10px] uppercase mb-1 block" style={{ color: 'var(--text-disabled)' }}>Code *</label>
                  <Input data-testid="new-doc-type-code" value={newDocType.code} onChange={(e) => setNewDocType({ ...newDocType, code: e.target.value })} placeholder="e.g. PFD"
                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} className="text-sm" /></div>
                <div><label className="text-[10px] uppercase mb-1 block" style={{ color: 'var(--text-disabled)' }}>Name *</label>
                  <Input data-testid="new-doc-type-name" value={newDocType.name} onChange={(e) => setNewDocType({ ...newDocType, name: e.target.value })} placeholder="Process Flow Diagram"
                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} className="text-sm" /></div>
                <div><label className="text-[10px] uppercase mb-1 block" style={{ color: 'var(--text-disabled)' }}>Discipline</label>
                  <Input value={newDocType.discipline} onChange={(e) => setNewDocType({ ...newDocType, discipline: e.target.value })} placeholder="e.g. Process"
                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} className="text-sm" /></div>
                <div className="flex items-end gap-2">
                  <Button size="sm" onClick={handleAddDocType} disabled={addingDocType} className="bg-[#1E4E8C] hover:bg-[#2850EE] text-white" data-testid="confirm-add-doc-type-btn">
                    {addingDocType ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}</Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowAddDocType(false)} style={{ color: 'var(--text-muted)' }}>Cancel</Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-3">
        {documentTypes.map((dt) => {
          const config = getConfigForDocType(dt.id);
          const isExpanded = expandedType === dt.id;
          return (
            <div key={dt.id} className="rounded-xl overflow-hidden" data-testid={`doc-type-card-${dt.code}`}
              style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center justify-between p-4 cursor-pointer transition-colors" onClick={() => setExpandedType(isExpanded ? null : dt.id)}>
                <div className="flex items-center gap-3">
                  <span className="px-2 py-1 text-xs font-semibold rounded" style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)', border: '1px solid rgba(56,96,255,0.2)' }}>{dt.code}</span>
                  <div>
                    <h4 className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{dt.name}</h4>
                    <p className="text-xs" style={{ color: 'var(--text-disabled)' }}>{dt.discipline || 'No discipline'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {config ? (
                    <span className="text-xs flex items-center gap-1" style={{ color: 'var(--status-completed-text)' }}>
                      <Check className="w-3 h-3" /> {config.stage_gates.length} gates
                    </span>
                  ) : (
                    <span className="text-xs flex items-center gap-1" style={{ color: '#f59e0b' }}>
                      <AlertTriangle className="w-3 h-3" /> No config
                    </span>
                  )}
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteDocType(dt.id); }}
                    className="h-7 w-7 p-0" style={{ color: 'var(--text-disabled)' }}><Trash2 className="w-3.5 h-3.5" /></Button>
                  {isExpanded ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--text-disabled)' }} /> : <ChevronDown className="w-4 h-4" style={{ color: 'var(--text-disabled)' }} />}
                </div>
              </div>
              <AnimatePresence>
                {isExpanded && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="p-4" style={{ borderTop: '1px solid var(--border-color)' }}>
                      <StageGateEditor config={config} docType={dt} projectId={projectId}
                        onSaved={() => { setExpandedType(null); fetchData(); }} onCancel={() => setExpandedType(null)} />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
        {documentTypes.length === 0 && (
          <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>
            <Settings className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No document types defined</p>
            <p className="text-sm mt-1" style={{ color: 'var(--text-disabled)' }}>Add a document type to get started</p>
          </div>
        )}
      </div>
    </div>
  );
}
